import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowRight, Eye, EyeOff, Lock, Mail, ShieldCheck } from "lucide-react";
import { loginUser } from "../../../features/adminAuthSlice";
import ADMIN_PATHS from "../ADMIN_PATHS";

const CORPORATE_FONT_FAMILY = '"Segoe UI", "Helvetica Neue", Arial, sans-serif';

const LoginPage = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPass, setShowPass] = useState(false);
    const dispatch = useDispatch();
    const { loading, error } = useSelector((state) => state.adminAuth);
    const navigate = useNavigate();

    const canSubmit = useMemo(() => email.trim() && password.trim(), [email, password]);

    const inputBase =
        "w-full rounded-md border border-slate-900/20 bg-white px-11 py-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 " +
        "outline-none transition-all focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10";

    // Plain login attempt: credentials are only ever checked by POST /admin/login,
    // which returns the same generic "Invalid credentials" for unknown email and
    // wrong password. The previous pre-login GET /admin/lookup step confirmed admin
    // emails without authentication (enumeration) and was removed.
    const handleSubmit = async (e) => {
        e.preventDefault();

        const action = await dispatch(loginUser({ email, password }));
        if (loginUser.fulfilled.match(action)) {
            toast.success("Login successful.");
            setTimeout(() => {
                navigate(ADMIN_PATHS.DASHBOARD);
            }, 800);
            return;
        }

        const message = action.payload || action.error?.message || "Login failed";
        toast.error(message);
    };

    const buttonDisabled = loading || !canSubmit;

    return (
        <div className="w-full bg-white" style={{ fontFamily: CORPORATE_FONT_FAMILY }}>
            <div className="mx-auto flex min-h-screen max-w-[1240px] items-center justify-center px-3 py-8 sm:px-4 md:py-12 lg:px-6">
                <div className="w-full max-w-[520px] overflow-hidden rounded-md border border-slate-900/20 bg-white">
                    <section className="relative bg-white p-6 sm:p-8 md:p-9">
                        <div className="relative">
                            <h1
                                className="text-[1.9rem] leading-[1.03] text-slate-900"
                                style={{ fontFamily: '"Iowan Old Style", "Times New Roman", serif' }}
                            >
                                Sign in to Cherrfy internal.
                            </h1>

                            <p className="mt-3 text-sm leading-6 text-slate-600">
                                Manage orders, catalog, finance, and platform settings from one console.
                            </p>

                            <div className="mt-6 flex items-center gap-3">
                                <div className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-900/20 bg-white text-slate-700">
                                    <ShieldCheck size={18} />
                                </div>
                                <div className="h-px flex-1 bg-slate-200" />
                                <span className="text-xs font-medium text-slate-400">secure access</span>
                            </div>

                            {error ? (
                                <div role="alert" className="mt-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.13em]">Login failed</p>
                                    <p className="mt-1 text-sm font-medium">{error}</p>
                                </div>
                            ) : null}

                            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
                                <div>
                                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700">
                                        Admin Email
                                    </label>
                                    <div className="relative">
                                        <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                        <input
                                            type="email"
                                            id="email"
                                            placeholder="admin@cherrfy.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className={inputBase}
                                            autoComplete="username"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-700">
                                        Password
                                    </label>
                                    <div className="relative">
                                        <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                        <input
                                            type={showPass ? "text" : "password"}
                                            id="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Enter your password"
                                            className={`${inputBase} pr-12`}
                                            autoComplete="current-password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-700"
                                            onClick={() => setShowPass((v) => !v)}
                                            aria-label={showPass ? "Hide password" : "Show password"}
                                        >
                                            {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                                        </button>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between pt-1">
                                    <label className="flex cursor-pointer select-none items-center gap-2 text-xs font-medium text-slate-900">
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20"
                                        />
                                        Remember me
                                    </label>

                                    <div className="flex items-center gap-4">
                                        <Link
                                            to={ADMIN_PATHS.AUTH.FORGOT_PASSWORD}
                                            className="text-xs font-semibold text-slate-900 transition-colors hover:text-[#D2042D]"
                                        >
                                            Forgot password?
                                        </Link>
                                        <Link
                                            to={ADMIN_PATHS.STORE}
                                            className="text-xs font-semibold text-slate-900 transition-colors hover:text-[#D2042D]"
                                        >
                                            Back to Store
                                        </Link>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={buttonDisabled}
                                    className={
                                        "group flex w-full items-center justify-center gap-2 rounded-md py-3 text-sm font-semibold transition-all " +
                                        (buttonDisabled
                                            ? "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400"
                                            : "border border-slate-900 bg-slate-900 text-white hover:bg-black")
                                    }
                                >
                                    {loading ? "Signing in..." : "Sign in"}
                                    {!loading ? <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" /> : null}
                                </button>
                            </form>

                            <div className="mt-6 border-t border-slate-200 pt-4">
                                <p className="text-xs font-medium leading-relaxed text-slate-600">
                                    Internal access is monitored and limited to approved admin accounts.
                                </p>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
